import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Lightbulb, Frown } from "lucide-react";
import { toast } from "sonner";

export const COMPLAINT_FIELDS = [
  { key: "patient_name", label: "Patient Name", type: "text" },
  { key: "phone", label: "Phone", type: "text" },
  { key: "branch", label: "Branch", type: "branch" },
  { key: "concern_type", label: "Type", type: "select", options: ["Complaint", "Complication"] },
  { key: "concern_text", label: "Concern", type: "textarea", full: true },
  { key: "responsible_person", label: "Responsible Person", type: "select", options: ["Manager", "Owner", "Doctor", "CEO"] },
  { key: "status", label: "Status", type: "select", options: ["Pending", "In Process", "Resolved"] },
  { key: "remark", label: "Remark", type: "textarea", full: true },
];
export const SUGGESTION_FIELDS = [
  { key: "patient_name", label: "Patient Name", type: "text" },
  { key: "phone", label: "Phone (optional)", type: "text" },
  { key: "suggestion_text", label: "Suggestion", type: "textarea", full: true },
  { key: "date", label: "Date", type: "date" },
  { key: "status", label: "Status", type: "select", options: ["Not Implemented", "In Progress", "Implemented"] },
  { key: "remark", label: "Remark", type: "textarea", full: true },
];
export const UNHAPPY_FIELDS = [
  { key: "patient_name", label: "Patient Name", type: "text" },
  { key: "reason_category", label: "Reason Category", type: "select", options: ["Waiting Time", "Doctor Behaviour", "Staff Behaviour", "Cost-Pricing", "Treatment Result", "Cleanliness", "Other"] },
  { key: "concern_text", label: "Reason", type: "textarea", full: true },
  { key: "branch", label: "Branch", type: "branch" },
  { key: "status", label: "Status", type: "select", options: ["Pending", "In Process", "Resolved"] },
];

function FieldRow({ f, value, onChange, meta }) {
  const common = "bg-[#0F172A] border-white/10 text-white";
  if (f.type === "textarea")
    return <Textarea data-testid={`qa-${f.key}`} className={common} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} />;
  if (f.type === "select")
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(f.key, v)}>
        <SelectTrigger data-testid={`qa-${f.key}`} className={common}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="bg-[#1E293B] border-white/10 text-white">
          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  if (f.type === "date")
    return <Input data-testid={`qa-${f.key}`} type="date" className={common} value={(value || "").slice(0, 10)} onChange={(e) => onChange(f.key, e.target.value)} />;
  if (f.type === "branch")
    return (<>
      <Input list="qa-branch-list" data-testid={`qa-${f.key}`} className={common} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} placeholder="Select or type branch" />
      <datalist id="qa-branch-list">{(meta.branches || []).map((b) => <option key={b} value={b} />)}</datalist>
    </>);
  return <Input data-testid={`qa-${f.key}`} className={common} value={value || ""} onChange={(e) => onChange(f.key, e.target.value)} />;
}

export function RecordDialog({ open, onOpenChange, title, endpoint, fieldDefs, initial, onSaved }) {
  const { person, meta, team } = useApp();
  const [form, setForm] = useState(initial || {});
  useEffect(() => { if (open) setForm({ ...(initial || {}) }); }, [open]); // eslint-disable-line
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const fb = form.entered_by || person || "";
    const body = { ...form, entered_by: fb, person: fb };
    try {
      if (form.id) await api.put(`/${endpoint}/${form.id}`, body);
      else await api.post(`/${endpoint}`, body);
      toast.success("Saved");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-light text-2xl">{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label className="text-slate-300 text-sm mb-1.5 block">Filled By</Label>
            <Select value={form.entered_by || ""} onValueChange={(v) => onChange("entered_by", v)}>
              <SelectTrigger data-testid="qa-filled-by" className="bg-[#0F172A] border-white/10 text-white"><SelectValue placeholder="Select staff" /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-72">
                {(team || []).map((t) => <SelectItem key={t.id} value={t.name}>{t.name}{t.role ? ` · ${t.role}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {fieldDefs.map((f) => (
            <div key={f.key} className={f.full ? "col-span-2" : ""}>
              <Label className="text-slate-300 text-sm mb-1.5 block">{f.label}</Label>
              <FieldRow f={f} value={form[f.key]} onChange={onChange} meta={meta} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:text-white">Cancel</Button>
          <Button data-testid="qa-save" onClick={save} className="bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useBranchDefault() {
  const { branch, person, team, meta } = useApp();
  const member = team.find((t) => t.name === person);
  return (branch && branch !== "All" ? branch : null) || member?.branch || member?.branches?.[0] || meta.branches?.[0] || "";
}

export function ComplaintButton({ onSaved, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const { person } = useApp();
  const br = useBranchDefault();
  const today = new Date().toISOString().slice(0, 10);
  return (<>
    <Button data-testid="add-complaint-btn" size={size} onClick={() => setOpen(true)} variant="outline"
      className="bg-[#DC2626]/10 border-[#DC2626]/30 text-[#f87171] hover:bg-[#DC2626]/20">
      <AlertTriangle className="h-4 w-4 mr-1.5" /> Add Complaint/Complication
    </Button>
    <RecordDialog open={open} onOpenChange={setOpen} title="Add Complaint / Complication" endpoint="concerns"
      fieldDefs={COMPLAINT_FIELDS} onSaved={onSaved}
      initial={{ concern_type: "Complaint", status: "Pending", branch: br, entered_by: person, date: today }} />
  </>);
}

export function SuggestionButton({ onSaved, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const { person } = useApp();
  const today = new Date().toISOString().slice(0, 10);
  return (<>
    <Button data-testid="add-suggestion-btn" size={size} onClick={() => setOpen(true)} variant="outline"
      className="bg-[#C9A24B]/10 border-[#C9A24B]/30 text-[#C9A24B] hover:bg-[#C9A24B]/20">
      <Lightbulb className="h-4 w-4 mr-1.5" /> Add Suggestion
    </Button>
    <RecordDialog open={open} onOpenChange={setOpen} title="Add Suggestion" endpoint="suggestions"
      fieldDefs={SUGGESTION_FIELDS} onSaved={onSaved}
      initial={{ status: "Not Implemented", entered_by: person, date: today }} />
  </>);
}

export function UnhappyButton({ onSaved, size = "sm" }) {
  const [open, setOpen] = useState(false);
  const { person } = useApp();
  const br = useBranchDefault();
  const today = new Date().toISOString().slice(0, 10);
  return (<>
    <Button data-testid="add-unhappy-btn" size={size} onClick={() => setOpen(true)} variant="outline"
      className="bg-[#DC2626]/10 border-[#DC2626]/30 text-[#f87171] hover:bg-[#DC2626]/20">
      <Frown className="h-4 w-4 mr-1.5" /> Add Unhappy Patient Reason
    </Button>
    <RecordDialog open={open} onOpenChange={setOpen} title="Add Unhappy Patient Reason" endpoint="concerns"
      fieldDefs={UNHAPPY_FIELDS} onSaved={onSaved}
      initial={{ concern_type: "Unhappy Reason", status: "Pending", branch: br, entered_by: person, date: today }} />
  </>);
}
